/**
 * List component controller
 * Component lists all order types (work, lookahead etc.)
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.71.0 OAUI-682 Rebuild list work/lookahead orders page and improve usability
 * 4.70.0 OAUI-678 Build "Driver Assignment" UI
 * 4.70.0 OAUI-677 Add total price column on work orders
 * 4.69.0 OAUI-677 Add total price column on work orders
 * 4.67.0 OAUI-660 The order-admin-ui redirects to order-admin-ui root before directing to the target URL
 * 4.67.0 OAUI-667 Add item progress to Work Orders
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.59.0 OAUI-640 Add adjustments without fulfillment groups
 * 4.55.0 OAUI-622 Fix updating job overrides in order details via orderApi
 * 4.49.0 OAUI-591 Restrict fabrication time, weight and pricing based on permissions
 * 4.48.0 OAUI-590 Enable column sorting for work orders and lookahead orders
 * 4.46.0 OAUI-577 Updating item shipping status shows "[object Object]"
 * 4.46.0 OAUI-572 Remove references to order-admin-api
 * 4.46.0 OAUI-575 Ecommerce orders on deployments with job override 'off' have display issues
 * 4.40.0 OAUI-544 If orderType is 'work' then fetch both lookahead and work orders
 * 4.38.0 OAUI-538 Local storage is not working as expected
 * 4.38.0 OAUI-537 Toggle table columns on order list table
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.37.0 OAUI-532 Add missing filters for v2 orders list
 * 4.37.0 OAUI-528 Sometime the page title on the list orders page doesn't translate
 * 4.37.0 OAUI-476 Add filter for order types
 * 4.36.0 OAUI-517 Work Orders V2: add UI for adding a lookahead order
 * 4.36.0 OAUI-523 Refactor order details view for Lookahead orders
 * 4.36.0 OAUI-506 Edit order job
 * 4.36.0 OAUI-502 Edit order details
 * 4.35.0 OAUI-496 Add header and missing fields
 * 4.35.0 OAUI-494 Update Ecommerce Job Overrides
 * 4.34.0 OAUI-491 Update order dates
 * 4.34.0 OAUI-489 Update multiple order statuses based on selection
 * 4.34.0 OAUI-488 Update fulfillment and shipping statuses
 * 4.34.0 OAUI-487 Update order status
 * 4.34.0 OAUI-479 Optimize the list orders page to avoid overloaded SCSS files
 * 4.33.0 OAUI-475 Quick fix to show ecommerce orders alongside internal
 * 4.33.0 OAUI-473 Toggle Empty Fulfillment Groups based on Setting
 * 4.33.0 OAUI-472 Add internal angular routing for view/manage order details
 * 4.33.0 OAUI-471 Add report/export links for orders to Work Orders V2
 * 4.33.0 OAUI-462 Display order fulfillment groups
 * 4.33.0 OAUI-463 API call issues with orders listing v2
 * 4.33.0 OAUI-468 Remove duplicate calls for constants and hostConfiguration
 * 4.31.0 OAUI-460 Disable and hide count down timer on Work Orders listing v2
 * 4.31.0 OAUI-455 Add basic table to display orders
 * 4.31.0 OAUI-453 Set up basic list settings, filters and layout
 * 4.31.0 OAUI-452 Set up basic list orders routing
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { WdOrdersService } from '../../../../services/orders/wd-orders.service';
import { WdLocalEnvironmentService } from '../../../../services/environment/wd-local-environment.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { WdLocalSharedFunctionsService } from '../../../../services/shared/wd-local-shared-functions.service';
import { WdOrdersV2Service } from '../../../../services/orders/wd-orders-v2.service';
import {
  IBcxErrorService,
  IBcxSharedFunctionsService,
} from '../../../../interfaces/generic/generic-interfaces';

@Component({
  selector: 'app-orders-list-component',
  templateUrl: './list.component.html',
  styleUrls: ['../../orders.component.scss', './list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListComponent implements OnDestroy, OnInit {
  /**
   * Constructor Initialization
   * @since 4.31.0
   */
  constructor(
    private translate: TranslateService,
    public activatedRoute: ActivatedRoute,
    public changeDetectorRef: ChangeDetectorRef,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    public wdOrdersService: WdOrdersService,
    public wdOrdersV2Service: WdOrdersV2Service,
    public router: Router,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
  ) {}

  // Basic Variables
  orderTitle = '';
  orderSubtitle = '';

  // Order Types
  orderType: string = this.wdLocalSharedFunctions.orderTypeKey.WORK;

  // Table Sorting
  @ViewChild(MatSort, { static: true }) sort: MatSort = new MatSort();

  // Order Items
  orderListDataSource = new MatTableDataSource();

  // Form
  @ViewChild('ordersListForm', { static: true })
  public ordersListForm!: NgForm;

  // Subscriptions
  subscriptions = new Subscription();

  // Current Order
  order: any;

  // Booleans
  isLoading = false;

  // Date Type Options
  dateTypes: any = {
    ordered: {
      key: 'ordered',
      label: 'Ordered',
    },
    production: {
      key: 'production',
      label: 'Production',
    },
    shipping: {
      key: 'delivery',
      label: 'Delivery',
    },
  };

  /**
   * Convert Date to Ngb Date Structure
   * @since 4.34.0
   */
  static toNgbDateStructure(date: Date) {
    return {
      day: date.getUTCDate(),
      month: date.getUTCMonth() + 1,
      year: date.getUTCFullYear(),
    };
  }

  /**
   * Returns true if the e-commerce job is editable
   * @since 4.46.0
   */
  canEditEcomJobOverride(element: any) {
    return (
      element.job?.override &&
      element.type === 'ecommerce' &&
      this.wdEnvironmentService.permissions.canEditEcommerceJob
    );
  }

  /**
   * Returns true if the read only job data can be shown in the order listing page
   * @since 4.46.0
   */
  canViewReadOnlyJob(element: any) {
    return (
      !element.job?.override ||
      element.type !== 'ecommerce' ||
      !this.wdEnvironmentService.permissions.canEditEcommerceJob
    );
  }

  /**
   * Update order status
   * @since 4.34.0
   */
  async updateFulfillmentGroupStatus(
    element: any,
    orderGroup: any,
  ): Promise<void> {
    orderGroup.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersV2Service.updateFulfillmentGroupStatus(
        element.index,
        orderGroup?.index,
        orderGroup?.status?.index,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersV2Service.fetchOrders(false);
      orderGroup.isUpdating = false;
    }
  }

  /**
   * Update order date
   * @since 4.34.0
   */
  async updateOrderDate(element: any, type: any, popover: any): Promise<void> {
    element[type.key].isUpdatingOrder = true;
    try {
      const ngbDate: NgbDateStruct = element[type.key].dateFormatted;
      if (ngbDate) {
        element[type.key].date = new Date(
          ngbDate.year,
          ngbDate.month - 1,
          ngbDate.day,
        );
        // Update Date
        await this.wdOrdersV2Service.updateOrderDate(
          element.index,
          type.key,
          'start',
          element[type.key].date,
        );
      }
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersV2Service.fetchOrders(false);
      element[type.key].isUpdatingOrder = false;
      popover.close();
    }
  }

  /**
   * Update order job override
   * @since 4.35.0
   */
  async updateOrderJobOverride(
    element: any,
    popover: any,
    type: string,
    label: string,
  ): Promise<void> {
    element.job.override['isUpdating' + label] = true;
    try {
      const overrideValue: string = element.job.override[type + 'Edit'];
      if (overrideValue) {
        // Endpoint requires both name and number
        const updateData: any = {
          jobId: element.job.index,
          jobName: element.job.override.name,
          jobNumber: element.job.override.number,
        };
        // Set updated value
        updateData['job' + label] = overrideValue;
        // Update order job override
        await this.wdOrdersV2Service.updateOrderJobOverride(
          element.index,
          updateData,
        );
      }
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersV2Service.fetchOrders(false);
      element.job.override['isUpdating' + label] = false;
      popover.close();
    }
  }

  /**
   * Update order status
   * @since 4.34.0
   */
  async updateOrderStatus(element: any): Promise<void> {
    element.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.wdOrdersV2Service.updateOrderStatus(
        element.index,
        element.status?.type,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersV2Service.fetchOrders(false);
      element.isUpdating = false;
    }
  }

  /**
   * Update order shipping status
   * @since 4.34.0
   */
  async updateShippingStatus(element: any): Promise<void> {
    element.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.wdOrdersV2Service.updateShippingStatus(
        element.index,
        element.delivery?.status?.index,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersV2Service.fetchOrders(false);
      element.isUpdating = false;
    }
  }

  /**
   * Close any popover
   *
   * @since 4.35.0
   * @memberof ListComponent
   */
  cancelPopover(popover: any) {
    popover.close();
  }

  /**
   * Initialize View Settings
   * @since 4.34.0
   */
  initializeSettings() {
    // Initialize Local Storage Defaults
    this.wdOrdersService.initializeLocalStorage();
    // Set Visible Order Columns
    this.wdOrdersService.initOrderListColumnsOptions(this.orderType);
    // Set Order Type Filter
    const allOrderTypes =
      this.wdLocalSharedFunctions.orderTypeKey.WORK +
      ',' +
      this.wdLocalSharedFunctions.orderTypeKey.LOOKAHEAD;
    // If Work Orders listing view, then fetch 'work' and 'lookahead' orders.
    // If Lookahead Orders listing view, then only fetch lookahead orders.
    this.wdOrdersV2Service.orderFilters.value.orderType =
      this.orderType === this.wdLocalSharedFunctions.orderTypeKey.WORK
        ? allOrderTypes
        : this.orderType;
    // Reset back to page one
    this.wdOrdersV2Service.orderViewSettings.page = 1;
  }

  /**
   * Is called by the view and returns whether the number of
   * selected products matches the total number of rows.
   * @since 4.31.0
   * @memberof ListComponent
   */
  isAllSelected(): boolean {
    const numSelected = this.wdOrdersService.orderListSelections.selected
      .length;
    const numRows = this.orderListDataSource.data.length;
    return numSelected === numRows;
  }

  /**
   * On Destroy
   * @since 4.31.0
   */
  ngOnDestroy() {
    // Unsubscribe from Changes
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
   * On Init
   * @since 4.37.0
   */
  ngOnInit() {
    // Subscribe to route changes and initialize accordingly
    this.subscriptions.add(
      this.activatedRoute.params.subscribe(async (params) => {
        // Get the order type from the route
        if (params.orderType) {
          this.orderType = params.orderType;
        }
        // Set Order Title
        this.setTitles();
        // Initialize Order Settings
        this.initializeSettings();
        // Watch Filters
        void this.wdOrdersV2Service.fetchOrders(true, true);
      }),
    );

    // Subscribe to Changes
    this.subscriptions.add(
      this.wdOrdersV2Service.allOrders.subscribe((orders: Array<any>) => {
        // Set Sort Values
        this.setColumnSortValues();
        // Set Order Events/Orders
        this.orderListDataSource.data = orders;
        // Set dynamic fulfillment group column sizing
        this.wdOrdersService.setFulfillmentGroupColumnSizing();
        // Begin Detecting UI Changes
        this.changeDetectorRef.detectChanges();
      }),
    );
  }

  /**
   * The label for the checkbox on the passed row
   * @since 4.31.0
   * @memberof ListComponent
   */
  setCheckboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'Deselect' : 'Select'} All Rows`;
    }
    return `${
      this.wdOrdersService.orderListSelections.isSelected(row)
        ? 'Deselect'
        : 'Select'
    } Order #${row.number}`;
  }

  /**
   * Set Table Sort Values
   * Manually set the sort value for each column (Required on columns where the source is an object)
   *
   * @since 4.48.0
   */
  setColumnSortValues(): void {
    // Set initial column sort value
    this.sort.active = 'number';
    this.sort.direction = 'desc';
    this.sort.sortChange.emit({
      active: this.sort.active,
      direction: this.sort.direction,
    });
    // Set column sorting
    this.orderListDataSource.sortingDataAccessor = (
      item: any,
      property: string,
    ) => {
      switch (property) {
        case 'lookahead':
          // Sort "Lookahead" column by lookahead label (a.k.a tag) (ignore case)
          return item?.tag?.toLowerCase();
        case 'job':
          // Sort "Job" column by job number (ignore case)
          return item?.job?.name?.toLowerCase();
        case 'user':
          // Sort "Ordered For" column by user last name (ignore case)
          return item?.orderedFor?.name?.last?.toLowerCase();
        case 'poNumber':
          // Sort "PO number" column by PO number (ignore case)
          return item?.poNumber?.toLowerCase();
        case 'tag':
          // Sort "Tag" column by tag (ignore case)
          return item?.tag?.toLowerCase();
        case 'groups':
          // Sort "Groups" column by active fulfillment groups
          return item?.fulfillment?.groups.filter(
            (group: any) => group?.status?.index,
          ).length;
        case 'ordered':
          // Sort "Ordered" Column by ordered date
          return Date.parse(item?.ordered?.date);
        case 'production':
          // Sort "Production" Column by production date
          return Date.parse(item?.production?.start?.date);
        case 'shipping':
          // Sort "Shipping" Column by delivery date
          return Date.parse(item?.delivery?.date);
        case 'weight':
          // Sort "Weight" Column by weight total
          return item?.totals?.weight?.value;
        case 'labour':
          // Sort "Labour" Column by shop labour value
          return item?.totals?.shopLabour?.value;
        case 'progress':
          // Sort "Progress" Column by item count
          return item?.meta?.items?.count;
        case 'price':
          // Sort "Price" Column by price total
          return item?.totals?.price?.value;
        default:
          // Sort column by column key
          return item[property];
      }
    };
    this.orderListDataSource.sort = this.sort;
  }

  /**
   * Set Order Page Title
   * @since 4.34.0
   */
  setTitles(): void {
    // Translate Titles
    this.translate
      .get('order.title.plural')
      .subscribe((resultTitle: string) => {
        this.orderSubtitle = resultTitle;
        // Translate Subtitle
        this.translate
          .get('order.types.' + this.orderType + '.title')
          .subscribe((resultSubtitle: string) => {
            this.orderTitle = [resultSubtitle, resultTitle].join(' ');
            // Set Window Title
            const title = this.orderSubtitle;
            const company = this.wdEnvironmentService.constants.getCompanyName;
            this.wdSharedFunctionsService.setWindowTitle(
              title + ' - ' + company,
            );
          });
      });
  }

  /**
   * Show fulfillment group for order
   * @since 4.59.0
   */
  showFulfillmentGroup(group: any, orderGroups: any[]): boolean {
    // Check if the group is filtered on or all group filters are off
    const groupFilteredOn = group?.selected; // Indicates if the specific group is selected/focused
    const groupFiltersAllOff = this.wdOrdersService.selectedFgs.length === 0; // Indicates if all group filters are off
    const isFilteredOn = groupFilteredOn || groupFiltersAllOff; // Determine if the group is filtered on or all group filters are off

    // Find a matching item based on the index property in orderGroups (if available)
    const matchingItem = orderGroups?.find(
      (oGroup: any) => oGroup.index === group.index,
    );

    // Determine the value of showGroup based on the matching item and its status index
    // If matchingItem exists and its status index is falsy, showGroup is set to the showEmpty value from wdOrdersService
    // Otherwise, showGroup is set to true
    let showGroup: boolean;
    if (matchingItem && matchingItem.status?.index) {
      showGroup = true;
    } else {
      showGroup = this.wdOrdersV2Service.orderViewSettings.groups.fulfillment
        .showEmpty;
    }

    // Return true if the group is filtered on and showGroup is true, otherwise return false
    return isFilteredOn && showGroup;
  }

  /**
   * Check order fulfillment group matches current group
   * @since 4.59.0
   */
  isMatchingFulfillmentGroup(group: any, orderGroup: any): boolean {
    return group?.index === orderGroup?.index;
  }

  /**
   * Check if the order groups have matching fulfillment group
   * @since 4.59.0
   */
  hasMatchingFulfillmentGroup(group: any, orderGroups: any[]): boolean {
    // Check if any order group matches the given group
    return (
      orderGroups &&
      orderGroups.some((orderGroup) => {
        return this.isMatchingFulfillmentGroup(group, orderGroup);
      })
    );
  }

  /**
   * Selects all fitting rows if they are not all selected; otherwise clear selection.
   * @since 4.31.0
   * @memberof ListComponent
   */
  toggleAllCheckboxes(): void {
    this.isAllSelected()
      ? this.wdOrdersService.orderListSelections.clear()
      : this.orderListDataSource.data.forEach((row) => {
          this.wdOrdersService.orderListSelections.select(row);
        });
  }

  /**
   * Toggle Manage Order Date Popover
   * @since 4.34.0
   * @memberof ListComponent
   */
  toggleOrderDatePopover(popover: any, order: any, type: any) {
    if (!popover.isOpen()) {
      const dateOrdered = new Date(order[this.dateTypes.ordered.key].date);
      const dateProduction = new Date(
        order[this.dateTypes.production.key].date,
      );
      const dateShipping = new Date(order[this.dateTypes.shipping.key].date);
      const dateTarget = new Date(order[type.key].date);

      let min = null;
      let max = null;

      // Set Min/Max for Production Date
      if (type.key === this.dateTypes.production.key) {
        min = ListComponent.toNgbDateStructure(dateOrdered); // Min must be after date ordered
        max = ListComponent.toNgbDateStructure(dateShipping); // Min must be before date shipping
      }

      // Set Min/Max for Shipping Date
      if (type.key === this.dateTypes.shipping.key) {
        min = ListComponent.toNgbDateStructure(dateProduction); // Min must be after date production
      }

      // Set Date and Open Popover
      order[type.key].dateFormatted = ListComponent.toNgbDateStructure(
        dateTarget,
      );
      popover.open({ order, type, popover, min, max });
    }
  }

  /**
   * Toggle Order Content Popover
   * Includes special Instructions, attachments count and adjustments count
   * @since 4.34.0
   * @memberof ListComponent
   */
  toggleOrderContentPopover(popover: any, order: any) {
    if (popover.isOpen()) {
      popover.close();
    } else {
      popover.open({ order });
    }
  }

  /**
   * Toggle Order Override Popover
   * Includes Job Name or Job Number Override
   * @since 4.35.0
   * @memberof ListComponent
   */
  toggleOrderOverridePopover(
    popover: any,
    order: any,
    type: string,
    label: string,
  ) {
    if (order.type === 'ecommerce') {
      if (!popover.isOpen()) {
        // Set Editable Value and Open Popover
        order.job.override[type + 'Edit'] = order.job.override[type];
        popover.open({ order, popover, type, label });
      }
    }
  }
}
